/**
 * Parser for Bedrock `.lang` files.
 *
 * Format: `key=value` per line. Comments start with `##`. Some packs use a
 * trailing `\t##comment`. We keep line numbers so conflict reports can point at
 * the exact source line.
 */

export interface LangLine {
  key: string;
  value: string;
  line: number; // 1-indexed
}

export interface ParsedLang {
  /** All key/value lines in file order (duplicates preserved). */
  lines: LangLine[];
  /** Duplicate keys found WITHIN this single file. */
  duplicatesInFile: { key: string; lines: number[] }[];
}

export function parseLang(content: string): ParsedLang {
  const lines: LangLine[] = [];
  const rawLines = content.split(/\r?\n/);
  rawLines.forEach((raw, i) => {
    const line = i + 1;
    const trimmed = raw.trim();
    if (!trimmed || trimmed.startsWith("#")) return;
    const eq = raw.indexOf("=");
    if (eq === -1) return;
    const key = raw.slice(0, eq).trim();
    let value = raw.slice(eq + 1);
    // Strip a trailing tab-comment (Bedrock convention: `value\t##comment`).
    const tabComment = value.indexOf("\t#");
    if (tabComment !== -1) value = value.slice(0, tabComment);
    lines.push({ key, value: value.trim(), line });
  });

  const byKey = new Map<string, number[]>();
  for (const l of lines) {
    const arr = byKey.get(l.key) ?? [];
    arr.push(l.line);
    byKey.set(l.key, arr);
  }
  const duplicatesInFile = [...byKey.entries()]
    .filter(([, ls]) => ls.length > 1)
    .map(([key, ls]) => ({ key, lines: ls }));

  return { lines, duplicatesInFile };
}

/** Derive a Bedrock locale code from a `.lang` file path, e.g. "en_US". */
export function localeFromPath(path: string): string {
  const base = path.split("/").pop() ?? path;
  return base.replace(/\.lang$/i, "");
}
